<template>
  <div class="row x-table-wrapper">
    <!-- Search filters -->
    <div
      class="col-xs-12 col-md-12 mid-spacing filter-list"
      v-if="enableFilter == true"
    >
      <x-filter
        v-if="enableFilter == true"
        :filterDefinitions="filterDefinitions"
        :defaultfilters="fetchParams.searchFilters"
        @update="updateFilters"
        @saveSearch="saveSearch"
        :disabled="loading"
      />
    </div>

    <!-- Display total count of search results, without pagination -->
    <div class="col-xs-12 col-md-12 mid-spacing" v-if="totalCount !== false">
      <h4>{{ totalCount }} {{ title }}</h4>
    </div>

    <!-- Search box -->
    <div class="col-md-4 mid-spacing" v-if="enableSearch == true">
      <div class="row items-end gutter-sm search-wrapper">
        <div class="col-md-10">
          <q-search
            class="search"
            :placeholder="$t('ui.xtable.search_placeholder')"
            autofocus
            clearable
            hide-underline
            color="secondary"
            :debounce="600"
            v-model="fetchParams.searchKeywords"
            @input="updateSearch()"
            :disabled="loading"
          />
        </div>
        <div class="col-md-2">
          <q-btn
            :disabled="loading"
            @click="updateSearch(null, true)"
            class="x-button"
            icon="arrow_forward"
            no-ripple
          />
        </div>
      </div>
    </div>

    <!-- Action and toggle column menus -->
    <div class="col-md-8 self-end mid-spacing x-table-menus">
      <div class="column-toggle-menu dropdown-select float-right">
        <q-table-columns
          class="columns"
          :label="$t('ui.xtable.toggle_columns')"
          color="secondary"
          v-model="visibleColumns"
          :columns="allColumns"
          :disabled="loading"
          @input="columnChange"
        />
      </div>
      <div
        v-if="actions.length > 0 && actionsLocation == 'menu'"
        class="actions-menu dropdown-select float-right"
      >
        <q-btn-dropdown
          class="x-table-actions-menu-button"
          :label="$t('ui.xtable.actions')"
          no-ripple
          no-caps
          :disabled="loading"
        >
          <q-list link>
            <q-item
              v-for="action in actions"
              :key="action.name"
              :separator="action.separator ? true : false"
              :class="action.class"
              @click.native="handleAction(action)"
              v-close-overlay
              :disabled="action.selectedOnly && selectedItems.length == 0"
            >
              <q-item-main :label="action.name" />
              <q-item-side v-if="action.icon" :icon="action.icon" right />
              <q-tooltip v-if="action.description">{{
                action.description
              }}</q-tooltip>
            </q-item>
          </q-list>
        </q-btn-dropdown>
      </div>
    </div>

    <!-- The data table -->
    <div class="x-table col-xs-12 col-sm-12 col-lg-12 col-xl-12">
      <q-table
        grid
        :title="title"
        :data="tableData"
        :key="key"
        :showLoader="loading"
        :disabled="loading"
        :pagination="fetchParams.pagination"
        @request="updatePagination"
        binary-state-sort
        :columns="allColumns"
        :visible-columns="visibleColumns"
        :selection="selectionType"
        :selected.sync="selectedItems"
        :separator="separator"
        :row-key="rowKey"
        :rows-per-page-label="$t('ui.xtable.page_size_label')"
        :no-data-label="emptyStateLabel"
        :rows-per-page-options="rowsPerPage"
        :class="tableClasses"
      >
        <q-tr slot="header" slot-scope="props" :props="props">
          <q-th
            v-if="selectionType != 'none'"
            class="column-selection"
            auto-width
          >
            <q-checkbox color="primary" v-model="props.selected" />
          </q-th>
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :class="col.classes"
          >
            {{ col.label }}
          </q-th>
          <q-th
            v-if="actions.length > 0 && actionsLocation == 'row'"
            class="column-standard text-center"
            auto-width
          >
            {{ $t("ui.xtable.actions") }}
          </q-th>
        </q-tr>

        <q-tr
          slot="body"
          slot-scope="props"
          :props="props"
          :class="rowsAreClickable ? 'cursor-pointer' : ''"
          @click.native="clickRow(props.row[rowKey])"
        >
          <q-td
            v-if="selectionType != 'none'"
            class="column-selection"
            auto-width
          >
            <q-checkbox color="primary" v-model="props.selected" />
          </q-td>

          <q-td
            v-for="col in props.cols"
            :key="col.name"
            :props="props"
            :class="col.classes"
            @click.native="clickCell(props.row[rowKey], col.name)"
          >
            <img class="cell-icon" v-if="col.icon" :src="col.icon"/>
            <span class="cell-text cell-empty" v-if="isEmpty(col.value)">{{ defaultValue(col) }}</span>
            <span class="cell-text" v-else-if="col.isUrl"><a target="_blank" :href="'http://' + col.value">{{ col.value }}</a></span>
            <span class="cell-text" v-else>{{ col.value }}</span>
          </q-td>

          <q-td
            v-model="props.actions"
            v-if="actions.length > 0 && actionsLocation == 'row'"
            auto-width
            class="actions-cell"
          >
            <span v-for="action in actions" :key="action.name || action.icon">
              <a
                v-if="/^yule-/.test(action.icon)"
                @click.stop="handleAction(action, [props.row[rowKey]])"
                class="x-table-yule-icon"
                :class="action.class"
              >
                <i class="icon" :class="action.icon"></i>
              </a>
              <q-btn
                v-else
                :icon="action.icon"
                :class="action.class"
                size="md"
                flat
                round
                no-ripple
                @click.native.stop="handleAction(action, [props.row[rowKey]])"
              >
              </q-btn>
              <q-tooltip
                v-if="action.description || action.name"
                >{{ action.description || action.name }}</q-tooltip
              >
            </span>
          </q-td>
        </q-tr>
      </q-table>
    </div>

    <!-- Save search parameters modal -->
    <modal
      :title="savedSearchParams.title"
      :open="savedSearchModalOpen"
      @save="saveSearchCommit()"
      @cancel="closeSavedSearchModal()"
    >
      <field v-model="savedSearchName" :label="savedSearchParams.label" />
    </modal>
  </div>
</template>

<script>
import i18n from "@/i18n";
import XFilter from "@/components/helpers/DataInput/XFilter";
import Permission from "@/classes/Permission";
import Modal from "@/components/helpers/Layout/NewModal";
import Field from "@/components/helpers/DataInput/Field";

export default {
  components: { XFilter, Modal, Field },
  name: "XTable",
  props: {
    title: {
      type: String,
      required: true,
      default: "xtable"
    },
    columns: {
      type: Array,
      required: true
    },
    rowKey: {
      type: String,
      default: "id"
    },
    rowsPerPage: {
      type: Array,
      default: () => {
        return [5, 7, 10, 15, 25, 50, 100, 200];
      }
    },
    tableData: {
      type: Array,
      default: () => {
        return [];
      }
    },
    rowsAreClickable: {
      type: Boolean,
      dafault: false,
    },
    enableSearch: {
      type: Boolean,
      default: true
    },
    searchKeywords: {
      type: String,
      default: ""
    },
    fetchOnSearchUpdate: {
      type: Boolean,
      default: true
    },
    enableFilter: {
      type: Boolean,
      default: false
    },
    filterDefinitions: {
      type: Array,
      default: () => {
        return [];
      }
    },
    filters: {
      type: Object,
      default: () => {
        return {};
      }
    },
    fetchOnFilterUpdate: {
      type: Boolean,
      default: true
    },
    showLoader: {
      type: Boolean,
      default: false
    },
    totalResults: {
      type: [Number, Boolean],
      default: false
    },
    actions: {
      type: Array,
      default: () => {
        return [];
      }
    },
    actionsLocation: {
      type: String,
      default: "menu" // menu or row
    },
    selectionType: {
      type: String,
      default: "none"
    },
    pagination: {
      type: Object,
      default: () => {
        return {};
      }
    },
    savedSearchParams: {
      type: Object,
      default: () => {
        return {
          enabled: false,
          title: i18n.t("ui.xtable.save_search_title"),
          label: i18n.t("ui.xtable.name"),
          saveFunction: null,
          saveEvent: null
        };
      }
    },
    emptyStateLabel: {
      type: String,
      default: i18n.t("ui.xtable.empty_state_label")
    },
    tableSettingsName: {
      type: String,
      default: "contacts"
    },
  },
  data() {
    return {
      fetchParams: {
        searchKeywords: "",
        searchFilters: {},
        pagination: this.paginationSettings()
      },
      tableClasses: [],
      allColumns: [],
      visibleColumns: [],
      separator: "horizontal",
      totalCount: 0,
      selectedItems: [],
      loading: this.showLoader,
      key: 0,
      savedSearchModalOpen: false,
      savedSearchName: ""
    };
  },
  beforeMount() {
    let storedTableVisibleColumns = this.$localStorage.get(
      this.storedTableVisibleColumns()
    );

    if (storedTableVisibleColumns) {
      storedTableVisibleColumns = storedTableVisibleColumns.split(",");
    }

    // Override the default icons used for column sorting
    this.$q.icon.table.arrowUp = "keyboard_arrow_up";

    // Merge the parent's pagination object with our defaults
    let merged = { ...this.fetchParams.pagination, ...this.pagination };
    // And then feed the merged object back to the parent
    this.updatePagination({ pagination: merged }, false);

    // Set fetch search parameters
    this.fetchParams.searchKeywords = this.searchKeywords;
    this.fetchParams.searchFilters = this.filters;

    if (this.selectionType != 'none') {
      this.tableClasses.push('has-sticky-column')
    }

    // Convert column structure to that expected by QTable
    this.columns.forEach(col => {
      // First, check if the logged-in user can see this column
      let canSee = true;
      if (col.credentials) {
        let tableName = this.title
          .toLowerCase()
          .trim()
          .replace(/\s+/, "_");
        canSee = Permission.can(
          null,
          "table." + tableName + "." + col.name + ".view"
        );
        delete col.credentials;
      }

      if (canSee) {
        let classes = col.classes || "";
        classes = classes.trim().split(/\s*[\s,]\s*/);
        if (classes[0] == "") {
          classes = [];
        }

        // Convert some attributes into classes
        if (col.isSticky === true) {
          classes.push("column-sticky");
          this.tableClasses.push('has-sticky-column')
        }
        delete col.isSticky;

        classes.push("column-standard");

        if (col.isLink === true) {
          classes.push("column-link");
        }
        delete col.isLink;

        if (col.isBold === true) {
          classes.push("column-bold");
        }
        delete col.isBold;

        // Determine which columns should be initially visible
        if (col.required !== true && col.hideByDefault != true) {
          this.visibleColumns.push(col.name);
        }
        delete col.hideByDefault;

        // Apply formatting to some column types
        if (col.type == "dateTime") {
          col.format = datetime => {
            return this.formatDateTimeNatural(datetime, { useTimeExpressions: false })
          }
        }
        delete col.type;

        // Set some defaults
        if (typeof col.sortable == "undefined") {
          col.sortable = true;
        }

        if (typeof col.align == "undefined") {
          col.align = "left";
        }

        // Dedup and join into string
        classes = [...new Set(classes)];
        col.classes = classes.join(" ");

        this.allColumns.push(col);
      }
    })

    this.tableClasses = [...new Set(this.tableClasses)]
    this.tableClasses = this.tableClasses.join(" ")

    if (storedTableVisibleColumns && storedTableVisibleColumns.length > 0) {
      this.visibleColumns = storedTableVisibleColumns;
    }
  },
  methods: {
    columnChange(visibleColumns) {
      this.$localStorage.set(this.storedTableVisibleColumns(), visibleColumns);
    },
    paginationSettings() {
      let storedTableSettings = JSON.parse(
        this.$localStorage.get(this.storedTableSettingsValue())
      );

      if (_.isEmpty(storedTableSettings)) {
        return {
          sortBy: null,
          descending: false,
          page: 1,
          rowsPerPage: 25
        };
      } else {
        return storedTableSettings;
      }
    },
    async handleAction(action, ids = []) {
      // If the selection type is single or multiple,
      // then the ids are available in the selectedItems array
      // Otherwise, they're passed directly to this handler
      if (this.selectionType != "none") {
        ids = [];
        if (this.selectedItems.length > 0) {
          this.selectedItems.forEach(item => {
            ids.push(item.id);
          });
        }
      }

      // If the action has provided an event, emit it
      if (action.event) {
        this.$emit(action.event, ids);
      }

      // If a handler function has been provided, call it
      if (typeof action.function == "function") {
        this.loading = true;

        try {
          await action.function(ids);
        } finally {
          this.loading = false;
        }
      }
    },
    isEmpty(value) {
      return value === null || value === undefined || value === ''
    },
    defaultValue(column) {
      return this.$t('ui.xtable.empty_cell_value', { name: column.label.toLowerCase() })
    },
    resetPagePaginationSettings() {
      let storedTableSettings = JSON.parse(
        this.$localStorage.get(this.storedTableSettingsValue())
      );

      storedTableSettings.page = 1;

      this.updatePagination({ pagination: storedTableSettings }, false);
    },
    updateFilters(filters) {
      this.resetPagePaginationSettings();

      this.fetchParams.searchFilters = filters;
      this.$emit("updateFilters", this.fetchParams.searchFilters);

      if (this.fetchOnFilterUpdate) {
        this.triggerFetch();
      }
    },
    updateSearch(searchKeywords = null, triggerFetch = false) {
      this.resetPagePaginationSettings();

      if (searchKeywords == null) {
        searchKeywords = this.fetchParams.searchKeywords;
      }
      this.$emit("updateSearch", searchKeywords);
      if (this.fetchOnSearchUpdate || triggerFetch) {
        this.triggerFetch();
      }
    },
    storedTableSettingsValue() {
      return this._.toLower(this.tableSettingsName) + "TableSettings";
    },
    storedTableVisibleColumns() {
      return this._.toLower(this.tableSettingsName) + "VisibleColumns";
    },
    updatePagination(payload, triggerFetch = true) {
      let payloadPagination = {
        sortBy: payload.pagination.sortBy,
        descending: payload.pagination.descending,
        page: payload.pagination.page,
        rowsPerPage: payload.pagination.rowsPerPage
      };

      this.$localStorage.set(
        this.storedTableSettingsValue(),
        JSON.stringify(payload.pagination)
      );

      this.fetchParams.pagination = payload.pagination;
      this.$emit("updatePagination", payload.pagination);
      if (triggerFetch) {
        this.triggerFetch();
      }
    },
    triggerFetch() {
      this.$emit("fetchData", this.fetchParams);
    },
    clickRow(id) {
      if (this.rowsAreClickable) {
        this.$emit("clickRow", id)
      }
    },
    clickCell(id, fieldName) {
      this.$emit("clickCell", id, fieldName)
    },
    openSavedSearchModal() {
      this.savedSearchName = "";
      this.savedSearchModalOpen = true;
    },
    closeSavedSearchModal() {
      this.savedSearchModalOpen = false;
    },
    saveSearch() {
      this.openSavedSearchModal();
    },
    saveSearchCommit() {
      this.closeSavedSearchModal();
      let payload = {
        name: this.savedSearchName,
        search: this.fetchParams
      };
      if (this.savedSearchParams.saveEvent) {
        this.$emit(this.savedSearchParams.saveEvent, payload);
      }
      if (typeof this.savedSearchParams.saveFunction == "function") {
        this.savedSearchParams.saveFunction(payload);
      }
    }
  },
  watch: {
    tableData: function() {

      // Reset item selections when the tabele data updates
      this.selectedItems = []

      // Force the table to re-render
      this.key++;

      // Set the 'left' attribute of sticky columns to their initial position
      // (There does not seem to be any pure CSS way to to this for non-left-most columns)
      this.$nextTick(() => {
        let stickyColumns = Array.from(
          document.getElementsByClassName("column-sticky")
        );

        stickyColumns.forEach(col => {
          if (col.offsetLeft != 0) {
            col.style.left = col.offsetLeft - 1 + "px";
          }
        });
      });
    },
    selectedItems: {
      deep: true,
      handler() {
        this.$emit("selectedItemsChange", this.selectedItems);
      }
    },
    totalResults: function() {
      this.totalCount = this.totalResults;
      this.fetchParams.pagination.rowsNumber = this.totalCount;
      this.$emit("emitTotalTableCount", this.totalCount);
    },
    showLoader: function() {
      this.loading = this.showLoader;
    }
  }
};
</script>

<style lang="sass">

.x-table-wrapper
  .float-right
    float: right
    padding-right: 10px

  .search
    margin: 10px

  // Re-arrange elements in footer
  .x-table
    .q-table-bottom
      div.q-table-control
        &:nth-of-type(1)
          display: none
        &:nth-of-type(3)
          position: absolute!important
          left: 0!important
          margin-left: 14px!important

  .q-table-container
    box-shadow: none
    .q-table-top
      display: none
    &.has-sticky-column
      .q-table-middle
        box-shadow: inset -5px 0px 30px 0 rgba(34, 48, 73, 0.2)
    .q-table-middle
      border-top: 1px solid $paper-dark
      border-right: 1px solid $paper-dark
      border-left: 1px solid $paper-dark

      table
      .q-table
        .q-option
          margin-top: -6px
          .text-faded
            color: $ink-lighter !important
          .text-grey-8
            color: $sensual !important
        thead
          background: $paper-lighter
          min-height: 58px
          border-bottom: 1px solid $paper-dark
          // Quasar v.17 has a bug which puts a TD element in the header in some cases,
          // so, we support both here
          th, td
            text-transform: uppercase
            letter-spacing: 2px
            padding: 10px 15px 6px 16px
            font-size: 12px
            font-weight: 500
            color: $ink-lighter
            &.column-selection
              position: sticky
              left: 0
              background-color: $paper-light
              z-index: 3
            &.column-sticky
              position: sticky
              background-color: $paper-light
              z-index: 1
              box-shadow: 0px 30px 30px 0 rgba(34, 48, 73, 0.2)
        tbody
          tr
            border-bottom: 1px solid $paper-lighter
            color: $ink-lighter
            &:hover
              background: $sensual-lighter
            .selected
              background: $sensual-lighter
            td
              height: 62px
              vertical-align: middle
              font-size: 14px
              padding: 8px 15px 8px 15px
              .q-option
                margin-top: -8px
              &.column-selection
                position: sticky
                left: 0
                background-color: #fff
                z-index: 3
              &.column-sticky
                position: sticky
                background-color: #fff
                z-index: 1
                box-shadow: 0px 30px 30px 0 rgba(34, 48, 73, 0.2)
              &.column-standard
                font-weight: $font-regular
                max-width: 300px
                white-space: nowrap
                overflow: hidden
                text-overflow: ellipsis
              &.column-link
                cursor: pointer
              &.column-bold
                text-decoration: none
                font-size: 14px
                line-height: 20px
                font-weight: $font-medium
                color: $sensual !important
                transition: all .15s ease-out
                &:hover
                  color: $sensual-dark !important
                &:active
                  color: $sensual-darker !important
              .cell-icon
                margin-right: 5px
                vertical-align: middle
              .cell-text
                line-height: 45px
                &.cell-empty
                  color: $paper-dark !important

  .mid-spacing
    margin: 0px 0px 30px 0px
    h4
      margin: 0px

  .search-wrapper
    .q-if
      border-bottom: 2px solid $paper
      padding-bottom: 5px
      margin: 0
    .q-if-focused
      border-bottom: 2px solid $sensual
    .q-if-focused:not(.q-if-readonly) .q-if-control
      color: $sensual

  .x-button
    border: 2px solid $paper !important
    background: $paper !important
    border-radius: 3px !important
    padding: 6px 15px 6px 15px !important
    font-size: 14px !important
    line-height: 24px !important
    min-height: 30px !important
    font-weight: $font-medium
    color: $ink-dark !important
    box-shadow: none !important
    text-transform: none !important
    transition: all .15s ease-out !important
    &:hover
      border: 2px solid $paper-dark !important
      background: $paper-dark !important
    &:active
      border: 2px solid $sensual !important
      background: $paper-light !important
    &:visited
      border: 2px solid $sensual !important
      background: $paper-light !important
    .q-focus-helper
      background-color: $paper !important
  @media (max-width: 340px)
    .x-button
      padding: 6px 0px 6px 4px !important
      font-size: 12px !important
  .dropdown-select
    button
      @extend .x-button
      padding: 6px 5px 6px 15px
    .q-if
      border: 2px solid $paper
      background: $paper
      border-radius: 3px
      padding: 6px 5px 6px 15px
      font-size: 14px
      line-height: 24px
      min-height: 40px
      font-weight: $font-medium
      transition: all .15s ease-out
      &:hover
        border: 2px solid $paper-dark
        background: $paper-dark !important
      .q-input-target
        color: $ink !important
    @media (max-width: 340px)
      .q-if
        padding: 6px 0px 6px 4px
        font-size: 12px
    .q-if-focused
      border: 2px solid $sensual
    .q-if-focused:not(.q-if-readonly) .q-if-control
      color: $sensual
      i.q-icon.q-if-control.material-icons
        color: $ink

  .navigation-dropdown
    margin-top: 0px
  .q-table-bottom
    color: $ink
    border: 0px
    padding: 20px 15px
    .q-table-control
      .q-if
        border: 2px solid $paper
        background: $paper
        border-radius: 3px
        padding: 6px 10px 6px 15px
        font-size: 14px
        font-weight: $font-medium
        color: $ink
      .q-if-focused
        border: 2px solid $sensual
      .q-if-focused:not(.q-if-readonly) .q-if-control
        color: $sensual
        i.q-icon.q-if-control.material-icons
          color: $ink

  // Force the column toggle menu and action menu to appear and behave the same
  .column-toggle-menu
    .q-select
      &:active
        border: 2px solid #1b88fd
      i
        color: rgb(33, 39, 54) !important

  // Set a bottom border that also includes the left column
  .x-table
    tr:last-child
      border-bottom-width: 0 !important
      td
        border-bottom-width: 0 !important
    .q-table-bottom
      border-top: 1px solid rgba(0, 0, 0, 0.12)

  // Prevent flipping of up/down arror
  .actions-menu
    i
      transform: matrix(1, 0, 0, 1, 0, 0) !important

  // Get rid of the hover/focus effect
  .actions-cell
    button, .q-focus-helper, .q-btn-inner, i
      background-color: transparent !important

  // Animations
  .x-filter-transition-enter-active,
  .x-filter-transition-leave-active
    transition: all 0.25s

  .x-filter-transition-enter,
  .x-filter-transition-leave-to
    opacity: 0

  // Empty state
  .x-table
    .q-table-nodata
      width: 100%
      display: block
      border: 1px solid $paper-dark
      border-top: none
      text-align: center
      font-size: 14px
      color: $ink-lighter
      font-weight: 500
      padding-bottom: 1em
      &:before
        content: ""
        background : url(../../../assets/empty-default.svg) no-repeat center
        display: block
        width: 144px
        height: 129px
        margin: 0 auto
        margin-bottom: 1em
      i
        display: none

  // Display yule icons in the same way as Material icons
  .x-table
    .x-table-yule-icon
      display: inline-flex
      height: 15px
      width: 42px
      vertical-align: text-bottom
      align-items: center
      color: $ink-lighter
      transition: all .15s ease-out
      &:hover
        color: $ink
      &:active
        color: $sensual
      i
        margin: 0 auto

  // Remove space inserted on right side of button, when no icon is used
  .x-table-menus
    .x-table-actions-menu-button
      i:nth-of-type(2)
        display: none

// Force 'toggle columns' menu to appear below button
.q-popover.column
  margin-top: 40px

.q-tooltip
  background-color: $ink-dark
  font-size: 14px
  font-weight: $font-medium
// Style actions menu
.q-popover
  box-shadow: 0 20px 66px 0 rgba(34,48,73,.2) !important
  .q-list
    padding: 5px 0px
    .q-item
      font-size: 14px
      font-weight: $font-medium
      color: $ink-light
      padding: 8px 30px
      .q-item-section
        .q-item-label
          padding: 0px
      &.q-select-highlight
        background: $paper-lighter !important
        color: $ink !important
      &:hover
        background: $paper-lighter
      .q-item-side
        .q-option
          .q-option-inner
            height: 20px
            padding: 0px
            width: 36px
            min-width: 36px
          .text-secondary
            color: $sensual !important
            &.active
              .q-toggle-handle
                left: 18px !important
            .q-toggle-base
              height: 20px
              opacity: 0.3
            .q-toggle-handle
              box-shadow: none !important
              top: 2px
              left: 2px
              width: 16px
              height: 16px
              line-height: 16px
</style>



// WEBPACK FOOTER //
// XTable.vue?7436de61